import { CalendarIcon, MarkerPin01Icon } from "@/icons";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { graphql, useFragment } from "react-relay";
import {
  EventLocationFragment$key,
  LocationKind,
} from "./__generated__/EventLocationFragment.graphql";
import { VDivided, VDividedItem } from "./Divided";
import { diffInDays } from "@/utils/date";

export const EventLocationFragment = graphql`
  fragment EventLocationFragment on Event {
    timeline {
      timezone
      startDate
      endDate
      locationKind
      location {
        country
        city
      }
    }
  }
`;

type DateStatus =
  | { tag: "Ongoing" }
  | { tag: "StartsIn"; days: number }
  | { tag: "Ended"; date: Date };

const resolveDateStatus = (
  startDate: string,
  endDate: string,
  timezone: string,
): DateStatus => {
  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone: timezone }),
  );
  const start = new Date(
    new Date(startDate).toLocaleString("en-US", { timeZone: timezone }),
  );
  const end = new Date(
    new Date(endDate).toLocaleString("en-US", { timeZone: timezone }),
  );

  if (now >= start && now <= end) return { tag: "Ongoing" };
  if (now < start) return { tag: "StartsIn", days: diffInDays(start, now) };
  return { tag: "Ended", date: new Date(endDate) };
};

const DateStatusLabel = ({ status }: { status: DateStatus }) => {
  const intl = useIntl();

  switch (status.tag) {
    case "Ongoing":
      return <FormattedMessage defaultMessage="Ongoing" />;

    case "StartsIn":
      return (
        <FormattedMessage
          defaultMessage="Starts in {days, plural, one {# day} other {# days}}"
          values={{ days: status.days }}
        />
      );

    case "Ended":
      return (
        <FormattedMessage
          defaultMessage="Ended: {date}"
          values={{
            date: (
              <FormattedDate
                value={status.date}
                day="numeric"
                month="short"
                year="numeric"
                timeZone={intl.timeZone}
              />
            ),
          }}
        />
      );
  }
};

interface EventLocationLabelProps {
  kind: LocationKind;
  country?: string;
  city?: string;
}

export function EventLocationLabel({
  kind,
  city,
  country,
}: Readonly<EventLocationLabelProps>) {
  switch (kind) {
    case "HYBRID":
      return <FormattedMessage defaultMessage="Hybrid" />;
    case "ONLINE":
      return <FormattedMessage defaultMessage="Online" />;
    case "IN_PERSON":
      if (!city && !country) return;
      return (
        <FormattedMessage
          defaultMessage="{city}, {country}"
          values={{ city, country }}
        />
      );
  }
}

interface EventLocationProps {
  location: EventLocationFragment$key;
}

export function EventLocation(props: EventLocationProps) {
  const { timeline } = useFragment(EventLocationFragment, props.location);

  if (!timeline) return null;

  const { location, locationKind, startDate, endDate, timezone } = timeline;
  const status =
    startDate && endDate
      ? resolveDateStatus(startDate, endDate, timezone ?? "UTC")
      : null;

  return (
    <VDivided>
      {status && (
        <VDividedItem icon={CalendarIcon}>
          <DateStatusLabel status={status} />
        </VDividedItem>
      )}
      {locationKind && (
        <VDividedItem icon={MarkerPin01Icon}>
          <EventLocationLabel
            kind={locationKind}
            city={location?.city}
            country={location?.country}
          />
        </VDividedItem>
      )}
    </VDivided>
  );
}
